import { isEmpty, isNil } from "lodash";

export const CHANGED_ATTRIBUTE = "data-kx-changed";

const MAX_COMPARED = 1500;

const IGNORED_ATTRIBUTES = [CHANGED_ATTRIBUTE];

const ownText = (element) =>
  Array.from(element.childNodes)
    .filter((node) => node.nodeType === 3)
    .map((node) => node.textContent.trim())
    .filter(Boolean)
    .join(" ");

const signatureOf = (element) => {
  const attributes = Array.from(element.attributes)
    .filter((attribute) => !IGNORED_ATTRIBUTES.includes(attribute.name))
    .map((attribute) => `${attribute.name}=${attribute.value}`)
    .sort()
    .join("|");
  return `${element.tagName}|${attributes}|${ownText(element)}`;
};

const isFullDocument = (html) => /<!doctype|<html/i.test(html || "");

const parse = (html) =>
  new DOMParser().parseFromString(
    isFullDocument(html) ? html : `<body>${html || ""}</body>`,
    "text/html",
  );

const serialized = (doc, html) =>
  isFullDocument(html)
    ? `<!DOCTYPE html>${doc.documentElement.outerHTML}`
    : doc.body.innerHTML;

const elementsOf = (doc) => Array.from(doc.body.querySelectorAll("*"));

const unmatched = (left, right) => {
  const rows = left.length;
  const columns = right.length;
  const table = Array.from(
    { length: rows + 1 },
    () => new Uint16Array(columns + 1),
  );
  for (let row = rows - 1; row >= 0; row -= 1)
    for (let column = columns - 1; column >= 0; column -= 1)
      table[row][column] =
        left[row] === right[column]
          ? table[row + 1][column + 1] + 1
          : Math.max(table[row + 1][column], table[row][column + 1]);
  const keptLeft = new Set();
  const keptRight = new Set();
  let row = 0;
  let column = 0;
  while (row < rows && column < columns) {
    if (left[row] === right[column]) {
      keptLeft.add(row);
      keptRight.add(column);
      row += 1;
      column += 1;
    } else if (table[row + 1][column] >= table[row][column + 1]) row += 1;
    else column += 1;
  }
  return {
    left: left.map((_, index) => index).filter((index) => !keptLeft.has(index)),
    right: right
      .map((_, index) => index)
      .filter((index) => !keptRight.has(index)),
  };
};

const flatRules = (rules) =>
  Array.from(rules || []).flatMap((rule) => [
    ...(rule.selectorText ? [rule] : []),
    ...flatRules(rule.cssRules),
  ]);

const rulesOf = (css) => {
  if (isEmpty(css)) return [];
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    return flatRules(sheet.cssRules);
  } catch (_) {
    return [];
  }
};

const selectorsOf = (rules) =>
  rules
    .map((rule) => rule.selectorText)
    .filter(Boolean)
    .flatMap((selector) => selector.split(","))
    .map((selector) => selector.replace(/::?[a-z-]+(\([^)]*\))?/gi, "").trim())
    .filter(Boolean);

const changedRules = (from, to) => {
  const known = new Set(rulesOf(from).map((rule) => rule.cssText));
  return rulesOf(to).filter((rule) => !known.has(rule.cssText));
};

const markSelectors = (doc, selectors) => {
  for (const selector of selectors) {
    try {
      for (const element of doc.body.querySelectorAll(selector))
        element.setAttribute(CHANGED_ATTRIBUTE, "");
    } catch (_) {
      continue;
    }
  }
};

const markElements = (elements, indexes) => {
  for (const index of indexes)
    elements[index].setAttribute(CHANGED_ATTRIBUTE, "");
};

export const markChanges = ({
  beforeHtml,
  afterHtml,
  beforeStyle,
  afterStyle,
}) => {
  const after = parse(afterHtml);
  if (isNil(beforeHtml))
    return {
      before: null,
      after: serialized(after, afterHtml),
      changed: false,
    };
  const before = parse(beforeHtml);
  const beforeElements = elementsOf(before);
  const afterElements = elementsOf(after);
  if (
    beforeElements.length <= MAX_COMPARED &&
    afterElements.length <= MAX_COMPARED
  ) {
    const gaps = unmatched(
      beforeElements.map(signatureOf),
      afterElements.map(signatureOf),
    );
    markElements(beforeElements, gaps.left);
    markElements(afterElements, gaps.right);
  }
  markSelectors(after, selectorsOf(changedRules(beforeStyle, afterStyle)));
  markSelectors(before, selectorsOf(changedRules(afterStyle, beforeStyle)));
  const changed =
    !isNil(after.body.querySelector(`[${CHANGED_ATTRIBUTE}]`)) ||
    !isNil(before.body.querySelector(`[${CHANGED_ATTRIBUTE}]`));
  return {
    before: serialized(before, beforeHtml),
    after: serialized(after, afterHtml),
    changed,
  };
};
